import { useCallback, useEffect, useState } from "react";
import { TIPS_VERSION } from "./tips";

const STORAGE_KEY = "ff.tips.dismissed";
export const TIPS_DISMISS_HOURS = 2;
const DISMISS_TTL_MS = TIPS_DISMISS_HOURS * 60 * 60 * 1000;

type DismissedState = {
  version: string;
  dismissedAt: number;
};

function parseDismissedState(raw: string | null): DismissedState | null {
  if (!raw) return null;
  if (raw === TIPS_VERSION) return { version: raw, dismissedAt: 0 };
  try {
    const parsed = JSON.parse(raw) as Partial<DismissedState>;
    if (typeof parsed.version === "string" && typeof parsed.dismissedAt === "number") {
      return { version: parsed.version, dismissedAt: parsed.dismissedAt };
    }
  } catch {
    return null;
  }
  return null;
}

function currentDismissed() {
  const state = parseDismissedState(localStorage.getItem(STORAGE_KEY));
  if (!state || state.version !== TIPS_VERSION) return false;
  if (state.dismissedAt === 0) {
    localStorage.removeItem(STORAGE_KEY);
    return false;
  }
  if (Date.now() - state.dismissedAt >= DISMISS_TTL_MS) {
    localStorage.removeItem(STORAGE_KEY);
    return false;
  }
  return true;
}

export function useTipsDismissed() {
  const [dismissed, setDismissed] = useState(currentDismissed);

  useEffect(() => {
    setDismissed(currentDismissed());
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: TIPS_VERSION, dismissedAt: Date.now() }));
    setDismissed(true);
  }, []);

  return { dismissed, dismiss };
}
